import { Component, OnDestroy, OnInit } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { ActivatedRoute } from "@angular/router";
import { Breadcrumb } from "@shared/components";
import { EMPTY, Subject, catchError, filter, finalize, of, switchMap, takeUntil, timer } from "rxjs";
import { NgBsToastService } from "ng-bs-toast-service";
import { ActiveConsultationSocketService } from "../active-consultation-socket.service";
import { ActiveConsultationStateService } from "../active-consultation-state.service";
import { ConsultationProcessedField, CurrentConsultationResponse, GRAVIDADE_1_A_5, Transcription } from "../consultations.model";
import { ConsultationsService } from "../consultations.service";

type ActiveProcessedField = "queixaPrincipal" | "historiaDaDoenca" | "antecedentesPessoaisFamiliares" | "medicacaoUsoContinuo" | "hipotesesDiagnostica" | "condutaMedica" | "insightNaoFeito";

@Component({
    selector: "app-active-consultation",
    standalone: false,
    templateUrl: "./active-consultation.component.html",
    styleUrl: "./active-consultation.component.css",
})
export class ActiveConsultationComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    private realtimeStop$ = new Subject<void>();
    private routeConsultationId = "";
    private realtimeStarted = false;
    private initialCurrentConsultationLoaded = false;

    loading = { main: true, transcriptions: false, physicalExam: false };
    consultation: CurrentConsultationResponse | null = null;
    transcriptions: Transcription[] = [];
    inactive = false;
    socketUnavailable = false;
    physicalExamInput = "";
    private physicalExamDirty = false;
    private lastSyncedPhysicalExam = "";

    breadcrumb: Breadcrumb[] = [
        { title: "Consultas", route: ["/consultations"] },
        { title: "Consulta em Andamento" },
    ];

    constructor(
        private activatedRoute: ActivatedRoute,
        private consultationsService: ConsultationsService,
        private socketService: ActiveConsultationSocketService,
        private activeConsultationState: ActiveConsultationStateService,
        private toastService: NgBsToastService,
    ) {}

    ngOnInit() {
        this.routeConsultationId = this.activatedRoute.snapshot.params["id"];

        this.activeConsultationState.activeConsultation$.pipe(
            takeUntil(this.destroy$),
        ).subscribe((consultation) => this.handleCurrentConsultation(consultation));

        this.activeConsultationState.refresh().pipe(
            takeUntil(this.destroy$),
        ).subscribe({
            next: (consultation) => {
                this.initialCurrentConsultationLoaded = true;
                if (!consultation || consultation.id !== this.routeConsultationId || consultation.finishedAt) {
                    this.inactive = true;
                    this.loading.main = false;
                }
            },
            error: () => {
                this.initialCurrentConsultationLoaded = true;
                this.inactive = true;
                this.loading.main = false;
            },
        });
    }

    ngOnDestroy() {
        this.realtimeStop$.next();
        this.realtimeStop$.complete();
        this.destroy$.next();
        this.destroy$.complete();
    }

    get patientName() {
        return this.consultation?.patient?.name || this.consultation?.patient?.nome || this.consultation?.paciente?.name || this.consultation?.paciente?.nome || this.consultation?.patientName || "---";
    }

    get patientSex() {
        return this.consultation?.patient?.sex || this.consultation?.patient?.sexo || this.consultation?.paciente?.sex || this.consultation?.paciente?.sexo || this.consultation?.patientSex || this.consultation?.sex || this.consultation?.sexo || "---";
    }

    getScore(score?: number | string) {
        const idx = Number(score);
        const config = idx ? GRAVIDADE_1_A_5[idx] : { title: "IDENTIFICANDO TRIAGEM...", color: "text-bg-secondary" };
        return `<span class="badge ${config.color}">${config.title}</span>`;
    }

    valueAsList(value?: ConsultationProcessedField) {
        if (!value) return [];
        const values = Array.isArray(value) ? value : [value];
        return values.filter((item) => this.hasValidClinicalValue(item));
    }

    hasClinicalValue(value?: ConsultationProcessedField) {
        return this.valueAsList(value).length > 0;
    }

    getProcessedFieldValue(field: ActiveProcessedField): ConsultationProcessedField {
        const value = this.consultation?.[field];

        if (Array.isArray(value)) {
            return value.map((item) => item.trim());
        }

        return value?.trim() ?? "";
    }

    isProcessedFieldList(value: ConsultationProcessedField): value is string[] {
        return Array.isArray(value);
    }

    isFieldAnnotating(field: ActiveProcessedField) {
        const value = this.getProcessedFieldValue(field);

        return Array.isArray(value)
            ? !value.some((item) => item)
            : !value;
    }

    trackTranscription(_: number, transcription: Transcription) {
        return transcription.transcription_id;
    }

    onPhysicalExamInputChange(value: string) {
        this.physicalExamInput = value;
        this.physicalExamDirty = value !== this.lastSyncedPhysicalExam;
    }

    savePhysicalExam() {
        if (!this.consultation?.id || this.loading.physicalExam) return;

        const value = this.physicalExamInput.trim();
        if (!value) return;

        const previousValue = this.consultation.exameFisico || "";
        this.loading.physicalExam = true;
        this.syncPhysicalExam(value);

        this.consultationsService.savePhysicalExam(this.consultation.id, value).subscribe({
            next: () => this.toastService.send("Exame físico salvo", "Os dados do exame físico foram enviados com sucesso.", "success"),
            error: (err: HttpErrorResponse) => {
                this.syncPhysicalExam(previousValue);
                this.toastService.send(`Error ${err.status}`, err.error?.error || err.name, "danger");
            },
        }).add(() => (this.loading.physicalExam = false));
    }

    private handleCurrentConsultation(consultation: CurrentConsultationResponse | null) {
        if (!consultation || consultation.id !== this.routeConsultationId || consultation.finishedAt) {
            if (!this.initialCurrentConsultationLoaded && !this.consultation) return;
            this.stopRealtimeStreams();
            this.inactive = true;
            this.loading.main = false;
            return;
        }

        this.consultation = { ...this.consultation, ...consultation };
        this.updatePhysicalExamInputFromRemote(consultation);
        this.inactive = false;
        this.loading.main = false;
        this.startRealtimeStreams();
    }

    private startRealtimeStreams() {
        if (this.realtimeStarted) return;
        this.realtimeStarted = true;

        timer(0, 10000).pipe(
            takeUntil(this.destroy$),
            takeUntil(this.realtimeStop$),
            switchMap(() => {
                this.loading.transcriptions = true;
                return this.consultationsService.getTranscriptions(this.routeConsultationId).pipe(
                    catchError(() => of(this.transcriptions)),
                    finalize(() => this.loading.transcriptions = false),
                );
            }),
        ).subscribe((transcriptions) => {
            this.transcriptions = [...transcriptions].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        });

        this.socketService.listenDiagnosis(this.routeConsultationId).pipe(
            takeUntil(this.destroy$),
            takeUntil(this.realtimeStop$),
            filter((payload) => this.isCurrentConsultationPayload(payload)),
            catchError(() => {
                this.socketUnavailable = true;
                return EMPTY;
            }),
        ).subscribe((payload) => {
            this.socketUnavailable = false;
            this.consultation = { ...this.consultation, ...payload };
            this.updatePhysicalExamInputFromRemote(payload);
        });
    }

    private isCurrentConsultationPayload(payload: Partial<CurrentConsultationResponse>) {
        const payloadId = payload.id || (payload as Record<string, string | undefined>)["consultationId"];
        return !payloadId || payloadId === this.routeConsultationId;
    }

    private stopRealtimeStreams() {
        if (!this.realtimeStarted) return;
        this.realtimeStop$.next();
        this.realtimeStarted = false;
    }

    private hasValidClinicalValue(value?: string) {
        const normalized = value?.trim();
        return !!normalized && normalized !== "---" && normalized !== "- - -";
    }

    private updatePhysicalExamInputFromRemote(consultation: Partial<CurrentConsultationResponse>) {
        if (!Object.prototype.hasOwnProperty.call(consultation, "exameFisico")) return;

        const normalizedValue = consultation.exameFisico || "";
        this.lastSyncedPhysicalExam = normalizedValue;
        if (!this.physicalExamDirty) {
            this.physicalExamInput = normalizedValue;
        }
    }

    private syncPhysicalExam(value: string) {
        this.consultation = { ...this.consultation, exameFisico: value };
        this.physicalExamInput = value;
        this.lastSyncedPhysicalExam = value;
        this.physicalExamDirty = false;
    }
}
